import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

type Effect = {
  id: string;
  type: "refresh" | "replace" | "notify" | "navigate";
  paths?: string[];
  target?: string;
  message?: string;
  url?: string;
  content?: string;
  redirect?: { from: string; to: string };
};
type PendingEffect = { effect: Effect; content?: DocumentFragment };

// Bootstrap on the existing document root, including visits that begin on a login layout.
export function mountUiEffects(): void {
  const root = document.documentElement;
  const events: Record<string, string> = {
    "ui-effects:effect": "receive", "turbo:visit": "visitStarted", "turbo:before-render": "renderStarted", "turbo:render": "renderFinished",
    "turbo:load": "pageChanged", "turbo:frame-load": "frameChanged", "modal:closed": "editorClosed",
    "ui-effects:editor-state-changed": "editorChanged", input: "trackEdit", change: "trackEdit",
    reset: "reset", "turbo:submit-end": "submitted",
  };
  root.dataset.controller = [...new Set([...(root.dataset.controller || "").split(/\s+/).filter(Boolean), "ui-effects"])].join(" ");
  const actions = Object.entries(events).map(([event, method]) => `${event}@document->ui-effects#${method}`);
  actions.push("hashchange@window->ui-effects#pageChanged");
  root.dataset.action = [...new Set([...(root.dataset.action || "").split(/\s+/).filter(Boolean), ...actions])].join(" ");
}

export default class extends Controller<HTMLElement> {
  private message = "";
  private visiting = false;
  private rendering = false;
  private processed = new Set<string>();
  private pending = new Map<string, PendingEffect>();
  private dirtyForms = new Set<HTMLFormElement>();
  private organizationId?: number;

  connect(): void {
    this.organizationId = this.pageContext().organization_id;
  }

  receive(event: CustomEvent<Effect>): void {
    const effect = event.detail;
    if (!effect.id || this.processed.has(effect.id)) return;
    this.processed.add(effect.id);
    const template = document.createElement("template");
    if (effect.type === "replace") template.innerHTML = effect.content || "";
    this.apply({ effect, content: template.content });
    this.renderNotice();
  }

  visitStarted(): void {
    this.visiting = true;
  }

  renderStarted(): void {
    this.rendering = true;
  }

  renderFinished(): void {
    this.rendering = false;
    if (!this.visiting) this.pageChanged();
  }

  frameChanged(): void {
    if (!this.visiting && !this.rendering) this.pageChanged();
  }

  trackEdit(event: Event): void {
    const field = event.target as HTMLElement;
    if (field.closest("[data-ui-effects-ignore]")) return;
    const form = field.closest("form");
    if (form) {
      form.dataset.uiEffectsDirtyForm = "true";
      this.dirtyForms.add(form);
    }
  }

  submitted(event: CustomEvent<{ success: boolean }>): void {
    if (!event.detail.success) return;
    this.clearForm(event.target as HTMLFormElement);
    this.retry();
  }

  reset(event: Event): void {
    this.clearForm(event.target as HTMLFormElement);
    this.retry();
  }

  editorClosed(event: Event): void {
    const container = event.target as HTMLElement;
    this.dirtyForms.forEach((form) => { if (container.contains(form)) this.clearForm(form); });
    const editors = [container, ...container.querySelectorAll<HTMLElement>("[data-ui-effects-dirty]")];
    editors.forEach((editor) => {
      if (editor.dataset.uiEffectsDirty) editor.dataset.uiEffectsDirty = "false";
    });
    this.retry();
  }

  editorChanged(event: Event): void {
    const editor = event.target as HTMLElement;
    if (editor.dataset.uiEffectsDirty === "false") {
      this.dirtyForms.forEach((form) => { if (editor.contains(form)) this.clearForm(form); });
    }
    this.retry();
  }

  pageChanged(): void {
    this.visiting = false;
    const organizationId = this.pageContext().organization_id;
    if (this.organizationId !== undefined && this.organizationId !== organizationId) {
      this.pending.clear();
      this.message = "";
    }
    this.organizationId = organizationId;
    this.dirtyForms = new Set(document.querySelectorAll<HTMLFormElement>('form[data-ui-effects-dirty-form="true"]'));
    this.retry();
  }

  retry(): void {
    const waiting = [...this.pending.values()];
    this.pending.clear();
    waiting.forEach((item) => this.apply(item));
    this.renderNotice();
  }

  dismiss(): void {
    this.message = "";
    this.renderNotice();
  }

  private clearForm(form: HTMLFormElement): void {
    delete form.dataset.uiEffectsDirtyForm;
    this.dirtyForms.delete(form);
  }

  private apply(item: PendingEffect): void {
    const { effect } = item;
    if (this.visiting || this.rendering || document.documentElement.hasAttribute("data-turbo-preview")) {
      this.pending.set(effect.id, item);
      return;
    }
    if (!this.matchesPage(effect)) return;
    if (this.wouldDiscardEdits(effect)) {
      this.pending.set(effect.id, item);

      this.dispatch("deferred", { detail: { id: effect.id } });
      return;
    }
    let applied = false;
    switch (effect.type) {
      case "refresh": applied = this.refresh(effect); break;
      case "replace": applied = this.replace(item); break;
      case "notify": applied = this.notify(effect.message); break;
      case "navigate": applied = this.navigate(effect.url); break;
    }
    if (applied) this.dispatch("applied", { detail: { id: effect.id } });
  }

  private matchesPage(effect: Effect): boolean {
    if (!effect.paths?.length) return true;
    const current = this.localUrl(this.pagePath());
    if (!current) return false;
    return effect.paths.some((path) => {
      const candidate = this.localUrl(path);
      if (!candidate) return false;
      const samePath = current.pathname === candidate.pathname || current.pathname.startsWith(`${candidate.pathname}/`);
      const sameParameters = [...candidate.searchParams].every(([key, value]) => current.searchParams.getAll(key).includes(value));
      return samePath && sameParameters;
    });
  }

  private wouldDiscardEdits(effect: Effect): boolean {
    if (effect.type === "notify") return false;
    const target = effect.target ? document.getElementById(effect.target) : null;
    const editors = Array.from(document.querySelectorAll<HTMLElement>('[data-ui-effects-dirty="true"]'));
    // Explicit markers also protect page editors in shared modals outside the frame.
    if (effect.type === "refresh" && editors.length > 0) return true;
    return [...this.dirtyForms, ...editors].some((editor) => {
      if (!editor.isConnected) return false;
      return effect.type === "navigate" ||
        (effect.type === "refresh" && target?.tagName !== "TURBO-FRAME") ||
        target?.contains(editor) || !!(target && editor.contains(target));
    });
  }

  private refresh(effect: Effect): boolean {
    const path = this.pagePath();
    const current = this.localUrl(path);
    if (!current) return false;
    if (effect.redirect) {
      const from = this.localUrl(effect.redirect.from);
      const to = this.localUrl(effect.redirect.to);
      if (from && to && current.pathname.replace(/\.html$/, "") === from.pathname) {
        Turbo.visit(`${to.pathname}${to.search}`, { action: "replace" });
        return true;
      }
    }
    if (!effect.target) {
      Turbo.visit(path, { action: "replace" });
      return true;
    }
    const frame = document.getElementById(effect.target) as (HTMLElement & { reload?: () => void }) | null;
    if (frame?.tagName !== "TURBO-FRAME") return false;
    if (frame.getAttribute("src") === path && frame.reload) frame.reload();
    else frame.setAttribute("src", path);
    return true;
  }

  private replace({ effect, content }: PendingEffect): boolean {
    const target = effect.target && document.getElementById(effect.target);
    if (!target || !content) return false;
    target.replaceWith(content);
    return true;
  }

  private notify(message?: string): boolean {
    this.message = message || "";
    return true;
  }

  private navigate(path?: string): boolean {
    const url = path && this.localUrl(path);
    if (!url) return false;
    Turbo.visit(url.toString());
    return true;
  }

  private renderNotice(): void {
    const existing = document.getElementById("ui_effect_notice");
    if (!this.message && (this.pending.size === 0 || this.visiting || this.rendering)) {
      existing?.remove();
      return;
    }
    const flash = document.getElementById("flash");
    if (!flash) return;
    const labels = JSON.parse(flash.dataset.uiEffectsLabels || "{}");
    const notice = existing || document.createElement("div");
    notice.id = "ui_effect_notice";
    notice.className = "alert flex flex-col bg-[#1C2E30] text-white border-none";
    notice.setAttribute("role", "status");
    notice.setAttribute("aria-live", "polite");
    const text = this.message || labels.deferred;
    // Avoid replacing focused retry controls when a duplicate/deferred effect arrives.
    if (notice.dataset.message === text && notice.dataset.pending === String(this.pending.size > 0)) return;
    notice.dataset.message = text;
    notice.dataset.pending = String(this.pending.size > 0);
    notice.replaceChildren();
    const message = document.createElement("p");
    message.textContent = text;
    if (this.pending.size > 0) message.dataset.uiEffectsTarget = "deferred";
    notice.append(message);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "btn btn-outline btn-xs";
    button.textContent = this.pending.size > 0 ? labels.retry : labels.dismiss;
    button.dataset.action = this.pending.size > 0 ? "ui-effects#retry" : "ui-effects#dismiss";
    notice.append(button);
    if (!existing) flash.prepend(notice);
  }

  private pagePath(): string {
    return this.pageContext().path || window.location.href;
  }

  private pageContext(): { path?: string; organization_id?: number } {
    const marker = document.getElementById("page_context");
    return JSON.parse(marker?.dataset.context || "{}");
  }

  private localUrl(path: string): URL | undefined {
    try {
      const url = new URL(path, window.location.origin);
      return url.origin === window.location.origin ? url : undefined;
    } catch {
      return undefined;
    }
  }
}
